import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { plansService } from '../services/supabaseService';
import ProtectedRoute from '../components/ProtectedRoute';
import { Loader2, CheckCircle2, AlertCircle, ShieldCheck, Sparkles } from 'lucide-react';

const PhonePeUpiNotice = () => (
  <div
    className="relative overflow-hidden rounded-2xl border-2 border-amber-400/70 bg-gradient-to-br from-amber-500/25 via-fuchsia-500/20 to-cyan-400/20 px-4 py-4 text-center shadow-[0_0_24px_rgba(251,191,36,0.25)] animate-pulse"
    role="status"
  >
    <div className="relative flex flex-col items-center gap-2">
      <div className="flex items-center justify-center gap-2 text-amber-200">
        <Sparkles className="h-5 w-5 shrink-0 text-amber-300 drop-shadow-[0_0_8px_rgba(251,191,36,0.8)]" />
        <span className="text-xs font-black uppercase tracking-[0.2em] text-amber-300/95">Heads up</span>
        <Sparkles className="h-5 w-5 shrink-0 text-amber-300 drop-shadow-[0_0_8px_rgba(251,191,36,0.8)]" />
      </div>
      <p className="relative text-sm font-semibold leading-snug text-white sm:text-base">
        <span className="inline-block origin-center hover:scale-105 transition-transform">📱</span>{' '}
        PhonePe UPI Payments are failing now, please use other UPI payments!
      </p>
      <p className="text-[11px] font-medium text-amber-100/85">Try Google Pay, Paytm, or your bank app — same QR works!</p>
    </div>
  </div>
);

// Component to handle the Uropay button and its script lifecycle
const UropayButton = ({ apiKey, buttonId, amount, userId, successUrl }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    // 1. Clean up any existing uropay script to ensure a fresh scan
    const existingScript = document.getElementById('uropay-runtime-script');
    if (existingScript) existingScript.remove();

    // 2. Create the script element
    const script = document.createElement('script');
    script.id = 'uropay-runtime-script';
    script.src = 'https://cdn.uropay.me/uropay-embed.min.js';
    script.async = true;
    
    // 3. Append the script to the body
    document.body.appendChild(script);

    return () => {
      // Optional: Clean up script on unmount if needed
      // script.remove();
    };
  }, [buttonId, amount]); // Re-run if plan changes

  return (
    <div ref={containerRef} className="w-full">
      <a 
        href="#" 
        className="uropay-btn w-full block py-4 rounded-2xl bg-brand-pink text-white font-bold text-lg shadow-lg hover:bg-brand-pink-dark transition-all transform active:scale-[0.98] text-center" 
        data-uropay-api-key={apiKey} 
        data-uropay-button-id={buttonId} 
        data-uropay-environment="LIVE" 
        data-uropay-amount={amount} 
        data-uropay-merchant-order-id={userId}
        data-uropay-success-redirect-url={successUrl} 
        data-uropay-failure-redirect-url={successUrl}
      >
        Upgrade to Premium Now
      </a>
    </div>
  );
};

const PaymentPage = () => {
  const { user, userProfile, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    plansService.list().then(data => {
      const filtered = data.filter(p => 
        (p.amount_paise === 250000 && p.name.toLowerCase().includes('monthly')) || 
        (p.amount_paise === 500000 && p.duration_days === 365)
      );
      setPlans(filtered);
      setLoading(false);
    });
  }, []);

  // Poll for subscription status
  useEffect(() => {
    let interval;
    if (user?.id && userProfile?.subscriptionStatus !== 'premium') {
      interval = setInterval(async () => {
        try {
          const profile = await refreshProfile();
          const status = profile?.subscriptionStatus ?? profile?.subscription_status;
          if (['premium', 'active', 'trialing'].includes(String(status).toLowerCase())) {
            clearInterval(interval);
          }
        } catch (e) {}
      }, 10000);
    }
    return () => clearInterval(interval);
  }, [user?.id, userProfile?.subscriptionStatus, refreshProfile]);

  const handleSelectPlan = (planId) => {
    setSelectedPlanId(planId);
    setError('');
  };

  const selectedPlan = useMemo(() => plans.find(p => p.id === selectedPlanId), [plans, selectedPlanId]);

  if (userProfile?.subscriptionStatus === 'premium') {
    return (
      <ProtectedRoute>
        <div className="max-w-xl mx-auto px-4 py-16">
          <div className="bg-slate-800 p-12 rounded-3xl border border-white/10 text-center animate-in zoom-in duration-500">
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="h-12 w-12 text-green-500" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Membership Active!</h2>
            <p className="text-slate-400 mb-8">Your premium features are now active. Enjoy full access!</p>
            <button 
              onClick={() => navigate('/dashboard')}
              className="w-full py-4 bg-brand-pink text-white rounded-2xl font-bold hover:bg-brand-pink-dark transition-all shadow-lg shadow-brand-pink/20"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </ProtectedRoute>
    );
  }

  return (
    <ProtectedRoute>
      <div className="max-w-xl mx-auto px-4 py-16">
        <h1 className="font-heading font-bold text-3xl text-white mb-2 text-center">Membership Upgrade</h1>
        <p className="text-slate-400 text-center mb-10 text-sm">Select a plan to unlock all premium features</p>
        
        {error && (
          <div className="mb-6 p-4 rounded-2xl bg-red-500/10 text-red-400 text-sm flex items-center gap-2 border border-red-500/20 animate-in slide-in-from-top-2">
            <AlertCircle className="h-5 w-5 shrink-0" />
            {error}
          </div>
        )}

        <div className="space-y-6">
          <div className="grid gap-3">
            {loading ? (
              <div className="flex justify-center py-12">
                 <Loader2 className="h-10 w-10 text-brand-pink animate-spin" />
              </div>
            ) : (
              plans.map((plan) => {
                const isSelected = selectedPlanId === plan.id;
                return (
                  <div
                    key={plan.id}
                    onClick={() => handleSelectPlan(plan.id)}
                    className={`relative p-5 rounded-2xl border-2 cursor-pointer transition-all ${
                      isSelected 
                        ? 'border-brand-pink bg-brand-pink/10 shadow-lg shadow-brand-pink/10 scale-[1.01]' 
                        : 'border-white/5 hover:border-white/10 bg-slate-800/40'
                    }`}
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="font-bold text-lg text-white">{plan.name}</span>
                          {plan.duration_days > 30 && <span className="text-[9px] bg-green-500 text-white px-2 py-0.5 rounded-full font-black uppercase tracking-wider">Best Value</span>}
                        </div>
                        <p className="text-xs text-slate-400">{plan.duration_days} Days Full Access</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-black text-brand-pink">₹{(plan.amount_paise / 100).toLocaleString()}</p>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

            {selectedPlanId && (
            <div key={selectedPlanId} className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="p-8 bg-slate-800 rounded-3xl border border-white/10 shadow-2xl text-center">
                <h3 className="font-bold text-white mb-6">Complete Your Payment</h3>
                
                <div className="flex justify-center mb-4">
                  {userProfile?.subscriptionStatus === 'premium' ? (
                    <div className="w-full py-4 rounded-2xl bg-green-500/20 text-green-400 font-bold text-lg border border-green-500/30">
                      Plan Currently Active
                    </div>
                  ) : selectedPlan?.amount_paise === 250000 ? (
                    <UropayButton 
                      apiKey="EG3CSSHD4U28V3X3398MCM5WG6YAWGJS"
                      buttonId="VICTOR182224"
                      amount={2500}
                      userId={user?.id}
                      successUrl={window.location.origin + '/payment/success'}
                    />
                  ) : selectedPlan?.amount_paise === 500000 ? (
                    <UropayButton 
                      apiKey="EG3CSSHD4U28V3X3398MCM5WG6YAWGJS"
                      buttonId="XRAY244020"
                      amount={5000}
                      userId={user?.id}
                      successUrl={window.location.origin + '/payment/success'}
                    />
                  ) : null}
                </div>
                
                <p className="text-xs text-slate-400">
                  The UPI QR and UTR verification will open in a secure Uropay popup. 
                  After payment is verified, your account will be upgraded instantly.
                </p>
              </div>
            </div>
          )}
          
          <div className="flex items-center justify-center gap-2 py-4 text-slate-500">
             <ShieldCheck className="h-4 w-4 text-brand-pink" />
             <span className="text-[10px] font-medium uppercase tracking-widest text-slate-400">Secure Uropay Encrypted Checkout</span>
          </div>

          <PhonePeUpiNotice />
        </div>
      </div>
    </ProtectedRoute>
  );
};

export default PaymentPage;
